// The drag order (R1208, ui-map-interface.md §8 "Drag order").
//
// A finger has no right button, so the touch grammar gave an order in two
// taps: select the hull, then tap the target. The drag order is the one
// gesture a finger has for the right-click: press an own fleet, drag, and
// lift over the target. While the finger travels, the map draws the order
// the release will give, in the colour of that order.
//
// This leaf holds the three pure parts, and both surfaces read them:
//
//   - `ContextMission`, what the context command decides for a point. The
//     season map (`Commands.contextMission`) and the arcade match
//     (`ArcadeInput.contextMission`) fill it through the four setters below,
//     and the right-click, the tap and the drag all act on it, so the
//     preview can never promise an order the release does not give.
//   - `dragInk`, the colour a kind wears. It is the standing order's colour
//     of `FLEET_ORDER_COLORS`, so the preview and the line the order then
//     draws agree. A commit wears the commit gold, and the standing line
//     of a pledging hull wears it too (R1208).
//   - `DragOrderGesture`, the press state machine. It touches no DOM node,
//     no canvas and no clock, so `dragorder.test.ts` pins it. Its one
//     outside line is the nullable `log` hook of the touch log (R1497).
//
// `overChrome` is the one DOM read of the leaf: the test both surfaces put
// to a release point.

import { FLEET_ORDER_COLORS } from '../gen/presentation';
import { TAP_DEFAULTS } from '../ui/touch';
import { cellCenter } from '../world';
import { describeTarget, inTouchLog } from './touchlog';

/**
 * What the context command does at a point.
 *
 * - `move` — sail to the point (open water, a gunless hull on a hostile, a
 *   site that takes no other order).
 * - `focus` — chase and fire at one hostile hull (R544).
 * - `escort` — follow one friendly hull (R544).
 * - `gather` — cut a deposit, a Dredger's alone (R80).
 * - `return` — sail home and dock (the own hold).
 * - `commit` — pledge at a strategic object (R788), the season map's alone.
 */
export type ContextKind = 'move' | 'focus' | 'escort' | 'gather' | 'return' | 'commit';

/** One decision of the context command. The caller owns and reuses it. */
export interface ContextMission {
  kind: ContextKind;
  /**
   * The world point the preview ends at. It is the target's centre when the
   * decision took a target, and the pointer's own point otherwise.
   */
  wx: number;
  wy: number;
  /** True when the point is a target's centre and not the pointer's. */
  snapped: boolean;
  /** The hull of a focus or an escort, the object of a commit, else 0. */
  targetId: number;
  /** The deposit cell of a gather, else -1. */
  cell: number;
  /** A commit alone: false while the object's window is shut (R787). */
  open: boolean;
}

/** A fresh decision, which a caller allocates once and reuses. */
export function newContextMission(): ContextMission {
  return { kind: 'move', wx: 0, wy: 0, snapped: false, targetId: 0, cell: -1, open: true };
}

/** Sets a decision to a plain move to a point. Every fill starts here. */
export function moveTo(m: ContextMission, wx: number, wy: number): ContextMission {
  m.kind = 'move';
  m.wx = wx;
  m.wy = wy;
  m.snapped = false;
  m.targetId = 0;
  m.cell = -1;
  m.open = true;
  return m;
}

/**
 * Sets a decision that names a cell: the point snaps to the cell's centre.
 * The cell itself is not stored, because a gather alone carries it
 * (`toDeposit`).
 */
export function toCell(m: ContextMission, kind: ContextKind, cell: number): ContextMission {
  const [x, y] = cellCenter(cell);
  m.kind = kind;
  m.wx = x;
  m.wy = y;
  m.snapped = true;
  return m;
}

/** Sets a gather: the one decision that carries its cell, the deposit's (R80). */
export function toDeposit(m: ContextMission, cell: number): ContextMission {
  toCell(m, 'gather', cell);
  m.cell = cell;
  return m;
}

/**
 * Sets a decision that names a hull. The point snaps to where the hull is
 * drawn, `at`, when the fleet layer holds it, and stays the pointer's
 * point when it does not.
 */
export function toHull(
  m: ContextMission, kind: ContextKind, id: number, at: { x: number; y: number } | null | undefined,
): ContextMission {
  m.kind = kind;
  m.targetId = id;
  if (at) {
    m.wx = at.x;
    m.wy = at.y;
    m.snapped = true;
  }
  return m;
}

/**
 * The colour a kind wears (R1208).
 *
 * Each kind takes the colour of the standing order its release sets: a
 * focus draws in the attack red (R544), a return in the home blue, and so
 * on. A commit sails on a plain move, and the move ink would hide it among
 * the other moves, so it takes the commit gold of the same table, which
 * the standing line of a pledging hull wears too.
 */
export function dragInk(kind: ContextKind): number {
  return FLEET_ORDER_COLORS[INK_OF[kind]].color;
}

/** The standing order whose colour each kind wears. */
const INK_OF: Record<ContextKind, keyof typeof FLEET_ORDER_COLORS> = {
  move: 'move', focus: 'attack', escort: 'escort',
  gather: 'gather', return: 'home', commit: 'commit',
};

/**
 * Is a screen point over the DOM chrome? A drag that lifts there gives no
 * order (R1208): that is how a finger backs out of one. The chip of the
 * touch log is no chrome, because a log must not change the gesture it
 * records (R1524).
 */
export function overChrome(
  canvas: Element, x: number, y: number, log: ((line: string) => void) | null,
): boolean {
  if (typeof document.elementFromPoint !== 'function') return false;
  const el = document.elementFromPoint(x, y);
  const over = el !== null && el !== canvas && !inTouchLog(el);
  if (log) log(`release overChrome=${over} el=${describeTarget(el)}`);
  return over;
}

/**
 * The press state machine of the drag order.
 *
 * The caller decides that a press is a drag candidate (a touch or pen press
 * on an own fleet, with no pick mode armed) and calls `press`. The rules:
 *
 * - Travel over the slop starts the drag, and `onStart` fires once. A
 *   release inside the slop is not a drag: the tap grammar owns it.
 * - While the drag runs, every move of the pressing pointer fires `onMove`.
 * - A release fires `onDrop` at the release point, with the fleet the
 *   press came down on. A release over the DOM
 *   chrome (`overChrome`) fires `onCancel` instead, so a finger can back
 *   out of a drag by lifting on a panel.
 * - A second pointer, a `pointercancel` and a `reset` void the press, and
 *   fire `onCancel` when the drag had started.
 */
export class DragOrderGesture {
  onStart: (x: number, y: number) => void = () => {};
  onMove: (x: number, y: number) => void = () => {};
  onDrop: (x: number, y: number, fleetId: number) => void = () => {};
  onCancel: () => void = () => {};
  /**
   * The touch log's hook (R1497), or null while the log is off. Each
   * transition writes one line through it; with null it builds no string.
   */
  log: ((line: string) => void) | null = null;

  /**
   * The pressing pointer, or null while no press is held. A pointer id can
   * be negative (iOS Safari on a device), so no id value means "no press".
   */
  private pointer: number | null = null;
  private x0 = 0;
  private y0 = 0;
  private slop = 0;
  private live = false;
  private fleet = 0;
  /** Where the pointer is now, in CSS px. The frame reads it. */
  x = 0;
  y = 0;

  /** True while a candidate press is held, dragging or not. */
  get held(): boolean { return this.pointer !== null; }
  /** True once the press travelled over the slop. */
  get dragging(): boolean { return this.live; }
  /** The fleet the press came down on, or 0. */
  get fleetId(): number { return this.pointer !== null ? this.fleet : 0; }

  /** Is this pointer the one that holds the press? */
  owns(pointerId: number): boolean { return this.pointer !== null && pointerId === this.pointer; }

  /**
   * The claim both surfaces hand the camera at `pointerdown` (R1208).
   *
   * A finger or a pen that presses an own fleet holds the press: `fleetAt`
   * names that fleet, or 0, and `select` selects it. A mouse keeps the
   * right-click and is never claimed, and a press while one is held is
   * left to the camera, so a second finger pinches. A pen taps through
   * the mouse grammar, so it keeps the click's slop, `penSlopPx`; a finger
   * keeps the tap's. `why` states for the touch log why `fleetAt` named
   * none, and only a log line calls it.
   *
   * Answers the handler the camera feeds the pointer's moves to, or null
   * when the press is not claimed.
   */
  claim(
    e: { readonly pointerId: number; readonly pointerType: string }, x: number, y: number,
    penSlopPx: number,
    fleetAt: (x: number, y: number) => number,
    why: (x: number, y: number) => string,
    select: (fleetId: number) => void,
  ): ((x: number, y: number) => void) | null {
    const log = this.log;
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') {
      if (log) log(`claim none: pointerType=${e.pointerType}`);
      return null;
    }
    if (this.held) {
      if (log) log('claim none: a drag press is already held');
      return null;
    }
    const id = fleetAt(x, y);
    if (id === 0) {
      if (log) log(`claim none: ${why(x, y)}`);
      return null;
    }
    if (log) log(`claim press fleet=${id}`);
    this.press(e.pointerId, x, y, id, e.pointerType === 'pen' ? penSlopPx : TAP_DEFAULTS.slopPx);
    select(id);
    const pointerId = e.pointerId;
    return (cx, cy) => this.move(pointerId, cx, cy);
  }

  /** A candidate press came down on an own fleet. */
  press(pointerId: number, x: number, y: number, fleetId: number, slopPx: number): void {
    this.reset();
    this.pointer = pointerId;
    this.x0 = this.x = x;
    this.y0 = this.y = y;
    this.slop = slopPx;
    this.fleet = fleetId;
    this.live = false;
    if (this.log) this.log(`drag press id=${pointerId} fleet=${fleetId} slop=${slopPx}`);
  }

  /**
   * Some pointer came down. A pointer that is not the pressing one is a
   * second finger, and it voids the press. Answers true when it did.
   */
  down(pointerId: number): boolean {
    if (this.pointer === null || pointerId === this.pointer) return false;
    if (this.log) this.log(`drag void: second pointer id=${pointerId}`);
    this.reset();
    return true;
  }

  move(pointerId: number, x: number, y: number): void {
    if (!this.owns(pointerId)) return;
    this.x = x;
    this.y = y;
    if (!this.live) {
      if (Math.hypot(x - this.x0, y - this.y0) <= this.slop) return;
      this.live = true;
      if (this.log) this.log(`drag start @${Math.round(x)},${Math.round(y)}`);
      this.onStart(x, y);
      return;
    }
    this.onMove(x, y);
  }

  /**
   * The pressing pointer lifted. Answers true when the release ended a
   * drag, so the caller knows the tap grammar must not also read it.
   */
  up(pointerId: number, x: number, y: number, overChrome: boolean): boolean {
    if (!this.owns(pointerId)) return false;
    const fleet = this.fleet;
    if (!this.clear()) {
      if (this.log) this.log('drag up inside the slop: the tap grammar reads it');
      return false;
    }
    if (this.log) this.log(overChrome ? 'drag cancel: release over chrome' : 'drag drop');
    if (overChrome) this.onCancel();
    else this.onDrop(x, y, fleet);
    return true;
  }

  /** The browser took the pressing pointer away. */
  cancel(pointerId: number): void {
    if (!this.owns(pointerId)) return;
    if (this.log) this.log(`drag void: the browser took pointer id=${pointerId}`);
    this.reset();
  }

  /** Forgets the press, as a hidden page must (see `TapRecognizer.reset`). */
  reset(): void {
    const held = this.pointer !== null;
    if (this.clear()) {
      if (this.log) this.log('drag cancel');
      this.onCancel();
    } else if (held && this.log) this.log('drag reset before the start');
  }

  /** Lets the press go. Answers true when a drag was running. */
  private clear(): boolean {
    const was = this.live;
    this.pointer = null;
    this.live = false;
    this.fleet = 0;
    return was;
  }
}
